import { CampaignReference, Entity, EntityMap, Group, Topic, User } from '../types';

export enum RecommendationType {
  RecommendationByAlgorithm = 'RecommendationByAlgorithm',
  RecommendationByFavoriteGroup = 'RecommendationByFavoriteGroup',
  RecommendationByFollowingSenders = 'RecommendationByFollowingSenders',
  RecommendationByFollowingThreadStarter = 'RecommendationByFollowingThreadStarter',
  RecommendationByFollowingTopic = 'RecommendationByFollowingTopic',
  RecommendationByFollowingCampaign = 'RecommendationByFollowingCampaign',
  RecommendationByGroupMembership = 'RecommendationByGroupMembership',
  RecommendationByPromotion = 'RecommendationByPromotion',
  RecommendationJustPosted = 'RecommendationJustPosted',
  RecommendationByAnnouncement = 'RecommendationByAnnouncement',
  RecommendationByTrending = 'RecommendationByTrending',
  RecommendationByInteractWithThreadStarter = 'RecommendationByInteractWithThreadStarter',
  RecommendationByInteractWithGroup = 'RecommendationByInteractWithGroup',
  RecommendationByInteractWithTopic = 'RecommendationByInteractWithTopic',
  RecommendationByInteractWithCampaign = 'RecommendationByInteractWithCampaign',
  RecommendationByNetworkLeaderCommenter = 'RecommendationByNetworkLeaderCommenter',
  RecommendationByUserLeaderCommenter = 'RecommendationByUserLeaderCommenter',
  RecommendationByUserLeaderThreadStarter = 'RecommendationByUserLeaderThreadStarter',
  RecommendationByNetworkLeaderThreadStarter = 'RecommendationByNetworkLeaderThreadStarter',
  RecommendationByContextWithCampaign = 'RecommendationByContextWithCampaign',
  RecommendationByContextWithCommenter = 'RecommendationByContextWithCommenter',
  RecommendationByContextWithGroup = 'RecommendationByContextWithGroup',
  RecommendationByContextWithThreadStarter = 'RecommendationByContextWithThreadStarter',
  RecommendationByContextWithTopic = 'RecommendationByContextWithTopic',
}

interface BaseRecommendation extends Entity {
  readonly type: RecommendationType;
}

export interface RecommendationByAlgorithm extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByAlgorithm;
}

export interface RecommendationByFavoriteGroup extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByFavoriteGroup;
  readonly groupId: Group['id'];
}

export interface RecommendationByFollowingSenders extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByFollowingSenders;
  readonly users: User['id'][];
}

export interface RecommendationByFollowingThreadStarter extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByFollowingThreadStarter;
  readonly user: User['id'];
}

export interface RecommendationByFollowingTopic extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByFollowingTopic;
  readonly topicIds: Topic['id'][];
}

export interface RecommendationByFollowingCampaign extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByFollowingCampaign;
  readonly campaignIds: CampaignReference['id'][];
}

export interface RecommendationByGroupMembership extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByGroupMembership;
  readonly groupId: Group['id'];
}

export interface RecommendationByPromotion extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByPromotion;
}

export interface RecommendationJustPosted extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationJustPosted;
}

export interface RecommendationByAnnouncement extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByAnnouncement;
}

export interface RecommendationByTrending extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByTrending;
}

export interface RecommendationByInteractWithThreadStarter extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByInteractWithThreadStarter;
  readonly userId: User['id'];
}

export interface RecommendationByInteractWithGroup extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByInteractWithGroup;
  readonly groupId: Group['id'];
}

export interface RecommendationByInteractWithTopic extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByInteractWithTopic;
  readonly topicIds: Topic['id'][];
}

export interface RecommendationByInteractWithCampaign extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByInteractWithCampaign;
  readonly campaignIds: CampaignReference['id'][];
}

export interface RecommendationByNetworkLeaderCommenter extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByNetworkLeaderCommenter;
  readonly users: User['id'][];
}

export interface RecommendationByUserLeaderThreadStarter extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByUserLeaderThreadStarter;
  readonly userId: User['id'];
}

export interface RecommendationByNetworkLeaderThreadStarter extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByNetworkLeaderThreadStarter;
  readonly userId: User['id'];
}

export interface RecommendationByUserLeaderCommenter extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByUserLeaderCommenter;
  readonly users: User['id'][];
}

export interface RecommendationByContextWithCampaign extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByContextWithCampaign;
  readonly campaignIds: CampaignReference['id'][];
}

export interface RecommendationByContextWithCommenter extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByContextWithCommenter;
  readonly userId: User['id'];
}

export interface RecommendationByContextWithGroup extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByContextWithGroup;
  readonly groupId: Group['id'];
}

export interface RecommendationByContextWithThreadStarter extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByContextWithThreadStarter;
  readonly userId: User['id'];
}

export interface RecommendationByContextWithTopic extends BaseRecommendation {
  readonly type: RecommendationType.RecommendationByContextWithTopic;
  readonly topicIds: Topic['id'][];
}

export type RecommendationReason =
  | RecommendationByAlgorithm
  | RecommendationByFollowingCampaign
  | RecommendationByFavoriteGroup
  | RecommendationByFollowingSenders
  | RecommendationByFollowingThreadStarter
  | RecommendationByFollowingTopic
  | RecommendationByGroupMembership
  | RecommendationByPromotion
  | RecommendationJustPosted
  | RecommendationByAnnouncement
  | RecommendationByTrending
  | RecommendationByInteractWithThreadStarter
  | RecommendationByInteractWithGroup
  | RecommendationByInteractWithTopic
  | RecommendationByInteractWithCampaign
  | RecommendationByNetworkLeaderCommenter
  | RecommendationByUserLeaderCommenter
  | RecommendationByUserLeaderThreadStarter
  | RecommendationByNetworkLeaderThreadStarter
  | RecommendationByContextWithCampaign
  | RecommendationByContextWithCommenter
  | RecommendationByContextWithGroup
  | RecommendationByContextWithThreadStarter
  | RecommendationByContextWithTopic;

export type RecommendationReasons = EntityMap<RecommendationReason>;
